import { useEffect, useState } from 'react';

/** Id of the synthetic select-all row; cannot collide with a real option. */
export const ALL_OPTION_ID = '__all__';

/**
 * Whether two selections hold the same ids, whatever their order.
 *
 * Sets rather than lengths plus `every`, so a repeat on one side cannot pass
 * for a distinct id on the other: `['a', 'a']` against `['a', 'b']` matches on
 * both counts otherwise. `selected` is read straight off the query string,
 * where nothing dedupes it (urlState reads it with `params.getAll`).
 */
const sameSelection = (a, b) => {
  const left = new Set(a);
  const right = new Set(b);

  return (
    left.size === right.size && Array.from(right).every((id) => left.has(id))
  );
};

/**
 * Staged multi-select over a KeyDS OptionsList.
 *
 * Two menus in User Management share this behaviour: the list page's filter menus
 * (PD-4063) and the Create User wizard's Accounts field (PD-4064). Both check
 * boxes into a draft that nothing outside sees until APPLY, both offer a
 * select-all row above the options, and both have to forget an abandoned draft
 * when they reopen.
 *
 * Selections are held as strings throughout, since that is what OptionsList
 * reports and what survives a URL round trip. Numeric option values (account ids)
 * are stringified on the way in.
 *
 * @param {object} params
 * @param {boolean} params.isOpen        reopening resets the draft to `selected`
 * @param {Array}   params.options       `{ label, value }` pairs, already narrowed
 * @param {Array}   params.selected      what is currently applied
 * @param {boolean} params.allOption     prepend a select-all row
 * @param {string}  params.allOptionLabel text for that row
 */
const useStagedSelection = ({
  allOption = false,
  allOptionLabel = 'All',
  isOpen,
  options = [],
  selected = [],
}) => {
  const appliedIds = selected.map(String);

  const [draft, setDraft] = useState(appliedIds);

  // Keyed on the contents rather than the array, because a caller that builds
  // `selected` inline hands over a new array on every render — which would reset
  // the draft on each keystroke and lose everything the user had checked.
  const appliedKey = JSON.stringify(appliedIds);

  // Reopening shows what is actually applied, discarding any abandoned draft.
  useEffect(() => {
    if (isOpen) setDraft(JSON.parse(appliedKey));
  }, [isOpen, appliedKey]);

  // What the footer's two buttons are gated on. They ask different questions:
  // APPLY offers to commit the draft, so it has nothing to do while the draft
  // still matches what is applied; CLEAR offers to empty the filter, so it has
  // nothing to do only when both sides are already empty.
  //
  // Compared as sets, because `draft` is ordered by the order rows were ticked
  // while `selected` arrives in the caller's order.
  const isDirty = !sameSelection(draft, appliedIds);

  const canClear = draft.length > 0 || appliedIds.length > 0;

  const visibleIds = options.map((option) => String(option.value));
  const allChecked =
    visibleIds.length > 0 && visibleIds.every((id) => draft.includes(id));

  const items = [
    ...(allOption ? [{ id: ALL_OPTION_ID, label: allOptionLabel }] : []),
    ...options.map((option) => ({
      id: String(option.value),
      label: option.label,
    })),
  ];

  const selectedIds =
    allOption && allChecked ? [ALL_OPTION_ID, ...draft] : draft;

  // The select-all row is a toggle rather than a value of its own: ticking it
  // takes every visible option, unticking it takes none.
  const handleChange = (ids) => {
    if (!allOption) {
      setDraft(ids);
      return;
    }

    const wantsAll = ids.includes(ALL_OPTION_ID);

    if (wantsAll && !allChecked) {
      setDraft(visibleIds);
      return;
    }

    if (!wantsAll && allChecked) {
      setDraft([]);
      return;
    }

    setDraft(ids.filter((id) => id !== ALL_OPTION_ID));
  };

  return {
    canClear,
    draft,
    handleChange,
    isDirty,
    items,
    selectedIds,
    setDraft,
  };
};

export default useStagedSelection;
